import type { UserCategory, UserListItemType } from "@nlc/shared-types";
import type { ColumnDef } from "@tanstack/react-table";
import type { SortDescriptor } from "react-stately";

import { USER_ROLES } from "@nlc/shared-types";
import { memo, useCallback, useEffect, useMemo, useRef } from "react";

import { Avatar } from "@/components/avatar";
import { Link } from "@/components/link";
import { StarRatingDisplay } from "@/components/star-rating";
import {
  type PaginationMetadata,
  DraggableTable as Table,
} from "@/components/table";
import { useTableSorting } from "@/hooks/useTableSorting";
import { useUser } from "@/hooks/useUser";
import { dateFormatter } from "@/utils/dateFormatter";

import { COLUMN_WIDTHS } from "../shared/columnWidths";
import { ImpersonateButton } from "../shared/components/ImpersonateButton";

interface ColumnConfig {
  always?: boolean;
  categories?: UserCategory[];
  excludeForRoles?: string[];
}

const COLUMN_CONFIGS: Record<string, ColumnConfig> = {
  account: {
    categories: ["customers", "all"],
    excludeForRoles: [USER_ROLES.OWNER, USER_ROLES.ADMIN],
  },
  actions: {
    categories: [
      "customers",
      "designers",
      "teamLeaders",
      "archived",
      "customerSuccess",
      "assistantTeamLeaders",
      "all",
    ],
    excludeForRoles: [USER_ROLES.OWNER, USER_ROLES.ADMIN],
  },
  archivedData: {},
  createdAt: { always: true },
  customerSuccess: {
    categories: ["customers"],
    excludeForRoles: [USER_ROLES.OWNER, USER_ROLES.ADMIN],
  },
  designation: {
    categories: [
      "customers",
      "designers",
      "teamLeaders",
      "assistantTeamLeaders",
    ],
  },
  email: {
    categories: [
      "customers",
      "teamLeaders",
      "superAdmins",
      "customerSuccess",
      "assistantTeamLeaders",
      "archived",
      "all",
    ],
  },
  name: { always: true },
  ratings: { categories: ["designers"] },
  role: { categories: ["all"] },
  team: { categories: ["all"] },
};

interface UsersTableProps {
  category: UserCategory;
  isLoading?: boolean;
  pagination?: {
    metadata: PaginationMetadata;
    onItemsPerPageChange?: (itemsPerPage: number) => void;
    onPageChange: (page: number) => void;
    pageSizeOptions?: number[];
    showPageSizeSelector?: boolean;
  };
  setSortDescriptor: (sortDescriptor: SortDescriptor) => void;
  sortDescriptor: SortDescriptor;
  users: UserListItemType[];
}

const UserNameCell = memo(function UserNameCell({
  user,
  userId,
}: {
  user: Pick<UserListItemType, "avatar" | "initials" | "name">;
  userId: string;
}) {
  const displayName = user.name || "No Name";

  return (
    <Link className="hover:no-underline" href={`/users/${userId}/edit`}>
      <div className="flex items-center py-3 pr-3 min-w-0 w-full overflow-hidden">
        <Avatar
          alt={displayName}
          className="size-10 rounded-full border border-border-secondary object-cover shrink-0"
          initials={user.initials}
          src={user.avatar || undefined}
        />
        <div className="ml-4 min-w-0 flex-1">
          <p className="text-sm font-medium text-text-primary leading-tight min-w-0 line-clamp-2 break-words">
            {displayName}
          </p>
        </div>
      </div>
    </Link>
  );
});

const TextCell = memo(function TextCell({
  className = "text-text-secondary",
  value,
}: {
  className?: string;
  value: null | string | undefined;
}) {
  return (
    <div className="py-3 pr-3 min-w-0 w-full overflow-hidden">
      <div
        className={`text-sm leading-tight line-clamp-2 break-words ${className}`}
      >
        {value || "-"}
      </div>
    </div>
  );
});

const DesignationCell = memo(function DesignationCell({
  user,
}: {
  user: Pick<UserListItemType, "designation" | "roleDisplayName">;
}) {
  const designationValue = user.designation || user.roleDisplayName || "-";

  return (
    <div className="py-3 pr-3 min-w-0 w-full overflow-hidden">
      <div className="text-sm leading-tight line-clamp-1 break-words text-text-secondary">
        {designationValue}
      </div>
    </div>
  );
});

const AccountNameCell = memo(function AccountNameCell({
  accountId,
  accountName,
}: {
  accountId: null | number | undefined;
  accountName: null | string | undefined;
}) {
  if (!accountId || !accountName) {
    return <TextCell value={null} />;
  }

  return (
    <div className="py-3 pr-3 min-w-0 w-full overflow-hidden">
      <Link
        className="text-sm leading-tight line-clamp-2 break-words"
        href={`/projects/${accountId}`}
        rel="noopener noreferrer"
        target="_blank"
      >
        {accountName}
      </Link>
    </div>
  );
});

const TeamNameCell = memo(function TeamNameCell({
  teamId,
  teamName,
}: {
  teamId: null | number | undefined;
  teamName: null | string | undefined;
}) {
  if (!teamId || !teamName) {
    return <TextCell value={null} />;
  }

  return (
    <div className="py-3 pr-3 min-w-0 w-full overflow-hidden">
      <Link
        className="text-sm leading-tight line-clamp-2 break-words"
        href={`/teams/${teamId}/edit`}
        rel="noopener noreferrer"
        target="_blank"
      >
        {teamName}
      </Link>
    </div>
  );
});

const RatingCell = memo(function RatingCell({
  value,
}: {
  value?: null | number;
}) {
  return (
    <div className="py-3 pr-3 min-w-0 w-full overflow-hidden">
      <StarRatingDisplay value={value} />
    </div>
  );
});

const DateCell = memo(function DateCell({ value }: { value: string }) {
  return (
    <div className="py-3 pr-3 min-w-0 w-full overflow-hidden">
      <span className="text-text-tertiary text-sm">{dateFormatter(value)}</span>
    </div>
  );
});

const ActionsCell = memo(function ActionsCell({
  role,
  userId,
}: {
  role: string;
  userId: string;
}) {
  if (role === USER_ROLES.SUPER_ADMIN) {
    return <div className="py-3 pr-3 min-w-0 w-full overflow-hidden" />;
  }

  return (
    <div className="py-3 pr-3 min-w-0 w-full overflow-hidden">
      <ImpersonateButton id={userId} />
    </div>
  );
});

const shouldShowColumn = (
  columnKey: string,
  category: UserCategory,
  userRole?: null | string,
): boolean => {
  const config = COLUMN_CONFIGS[columnKey];

  if (!config) return false;

  // Check if the column should be excluded for the current user's role
  if (userRole && config.excludeForRoles?.includes(userRole)) {
    return false;
  }

  return config.always || (config.categories?.includes(category) ?? false);
};

const createColumn = (
  key: string,
  header: string,
  cell: ColumnDef<UserListItemType>["cell"],
  options: {
    enableSorting?: boolean;
    width?: number;
  } = {},
): ColumnDef<UserListItemType> => ({
  accessorKey: key,
  cell,
  enableSorting: options.enableSorting ?? true,
  header,
  maxSize: options.width,
  minSize: options.width,
  size: options.width,
});

export const UsersTable = memo(function UsersTable({
  category,
  isLoading = false,
  pagination,
  setSortDescriptor,
  sortDescriptor,
  users,
}: UsersTableProps) {
  const tableRef = useRef<HTMLDivElement>(null);
  const { data: userData } = useUser();
  const currentUserRole = userData?.user?.roleName;

  const { handleSort, sorting } = useTableSorting({
    defaultColumn: "createdAt",
    defaultDirection: "descending",
    setSortDescriptor,
    sortDescriptor,
  });

  useEffect(() => {
    if (tableRef.current) {
      const table = tableRef.current.querySelector("table");

      if (table) {
        table.style.tableLayout = "fixed";
        table.style.width = "100%";
      }

      const cells = tableRef.current.querySelectorAll("td");

      for (const cell of cells) {
        cell.style.overflow = "hidden";
        cell.style.maxWidth = "0";
      }
    }
  }, [users]);

  const columns: ColumnDef<UserListItemType>[] = useMemo(() => {
    const columns: ColumnDef<UserListItemType>[] = [];

    if (shouldShowColumn("name", category, currentUserRole)) {
      columns.push(
        createColumn(
          "name",
          "Name",
          ({ row }) => (
            <UserNameCell user={row.original} userId={row.original.id} />
          ),
          { width: COLUMN_WIDTHS.NAME },
        ),
      );
    }

    if (shouldShowColumn("email", category, currentUserRole)) {
      columns.push(
        createColumn(
          "email",
          "Email",
          ({ getValue }) => <TextCell value={getValue() as string} />,
          { width: COLUMN_WIDTHS.EMAIL },
        ),
      );
    }

    if (shouldShowColumn("role", category, currentUserRole)) {
      columns.push(
        createColumn(
          "role",
          "Role",
          ({ row }) => <TextCell value={row.original.roleDisplayName} />,
          { width: COLUMN_WIDTHS.ROLE },
        ),
      );
    }

    if (shouldShowColumn("account", category, currentUserRole)) {
      columns.push(
        createColumn(
          "account",
          "Account name",
          ({ row }) => (
            <AccountNameCell
              accountId={row.original.accountId}
              accountName={row.original.accountName}
            />
          ),
          { width: COLUMN_WIDTHS.ACCOUNT },
        ),
      );
    }

    if (shouldShowColumn("team", category, currentUserRole)) {
      columns.push(
        createColumn(
          "team",
          "Team",
          ({ row }) => (
            <TeamNameCell
              teamId={row.original.teamId}
              teamName={row.original.teamName}
            />
          ),
          { width: COLUMN_WIDTHS.TEAM },
        ),
      );
    }

    if (shouldShowColumn("customerSuccess", category, currentUserRole)) {
      columns.push(
        createColumn(
          "customerSuccess",
          "Customer Success",
          ({ getValue }) => <TextCell value={getValue() as string} />,
          { width: COLUMN_WIDTHS.CUSTOMER_SUCCESS },
        ),
      );
    }

    if (shouldShowColumn("designation", category, currentUserRole)) {
      columns.push(
        createColumn(
          "designation",
          "Designation",
          ({ row }) => (
            <DesignationCell
              user={{
                designation: row.original.designation,
                roleDisplayName: row.original.roleDisplayName,
              }}
            />
          ),
          { width: COLUMN_WIDTHS.DESIGNATION },
        ),
      );
    }

    if (shouldShowColumn("ratings", category, currentUserRole)) {
      columns.push(
        createColumn(
          "rating",
          "Rating",
          ({ row }) => <RatingCell value={row.original.rating} />,
          { width: COLUMN_WIDTHS.RATING },
        ),
      );
    }

    if (shouldShowColumn("archivedData", category, currentUserRole)) {
      columns.push(
        createColumn(
          "archivedAt",
          "Archived Date",
          ({ getValue }) => {
            const value = getValue() as string;

            return value ? <DateCell value={value} /> : <TextCell value="-" />;
          },
          { width: COLUMN_WIDTHS.CREATED_ON },
        ),
        createColumn(
          "archivedBy",
          "Archived By",
          ({ getValue }) => {
            const value = getValue() as { name: string } | null;

            return <TextCell value={value?.name || "-"} />;
          },
          { width: COLUMN_WIDTHS.CREATED_ON },
        ),
      );
    }

    if (shouldShowColumn("createdAt", category, currentUserRole)) {
      columns.push(
        createColumn(
          "createdAt",
          "Created on",
          ({ getValue }) => <DateCell value={getValue() as string} />,
          { width: COLUMN_WIDTHS.CREATED_ON },
        ),
      );
    }

    if (shouldShowColumn("actions", category, currentUserRole)) {
      columns.push(
        createColumn(
          "actions",
          "Action",
          ({ row }) => (
            <ActionsCell role={row.original.role} userId={row.original.id} />
          ),
          { enableSorting: false, width: COLUMN_WIDTHS.ACTIONS },
        ),
      );
    }

    return columns;
  }, [category, currentUserRole]);

  const getRowId = useCallback((user: UserListItemType) => user.id, []);

  const paginationConfig = useMemo(
    () =>
      pagination
        ? {
            metadata: pagination.metadata,
            onItemsPerPageChange: pagination.onItemsPerPageChange,
            onPageChange: pagination.onPageChange,
            pageSizeOptions: pagination.pageSizeOptions,
            showPageSizeSelector: pagination.showPageSizeSelector,
          }
        : undefined,
    [
      pagination?.metadata,
      pagination?.onItemsPerPageChange,
      pagination?.onPageChange,
      pagination?.pageSizeOptions,
      pagination?.showPageSizeSelector,
    ],
  );

  const sortableColumns = useMemo(
    () => [
      "name",
      "email",
      "role",
      "account",
      "team",
      "createdAt",
      "customerSuccess",
      "designation",
      "rating",
    ],
    [],
  );

  return (
    <div className="rounded-b-xl bg-bg-primary overflow-auto" ref={tableRef}>
      <Table
        className="rounded-b-xl users-table w-full"
        columns={columns}
        data={users}
        getRowId={getRowId}
        isLoading={isLoading}
        isServerSideSorting
        onSort={handleSort}
        pagination={paginationConfig}
        sortableColumns={sortableColumns}
        sorting={sorting}
      />
    </div>
  );
});
